import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Power, Calendar, Tag } from 'lucide-react';
import { toast } from 'sonner';

const COLOR_OPTIONS = ['orange', 'rose', 'amber', 'emerald', 'violet'];
const COLOR_SWATCH = {
  orange: 'bg-orange-500',
  rose: 'bg-rose-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  violet: 'bg-violet-500',
};

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY = {
  festival_name: '',
  title: '',
  subtitle: '',
  discount_code: '',
  discount_percent: 10,
  badge_emoji: '🎉',
  banner_color: 'orange',
  start_date: '',
  end_date: '',
  is_active: true,
  sort_order: 0,
};

export const AdminCampaigns = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchCampaigns();
  }, [user, authLoading, navigate]);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/admin/campaigns');
      setCampaigns(res.data);
    } catch (e) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, sort_order: campaigns.length });
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      festival_name: c.festival_name,
      title: c.title,
      subtitle: c.subtitle || '',
      discount_code: c.discount_code || '',
      discount_percent: c.discount_percent || 0,
      badge_emoji: c.badge_emoji || '🎉',
      banner_color: c.banner_color || 'orange',
      start_date: toLocalInput(c.start_date),
      end_date: toLocalInput(c.end_date),
      is_active: !!c.is_active,
      sort_order: c.sort_order || 0,
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!form.festival_name || !form.title || !form.start_date || !form.end_date) {
      toast.error('Festival name, title and dates are required');
      return;
    }
    const payload = {
      ...form,
      start_date: new Date(form.start_date).toISOString(),
      end_date: new Date(form.end_date).toISOString(),
    };
    if (new Date(payload.end_date) <= new Date(payload.start_date)) {
      toast.error('End date must be after start date');
      return;
    }
    try {
      if (editing) {
        await api.put(`/admin/campaigns/${editing.id}`, payload);
        toast.success('Campaign updated');
      } else {
        await api.post('/admin/campaigns', payload);
        toast.success('Campaign created');
      }
      setShowDialog(false);
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/campaigns/${c.id}`, { is_active: !c.is_active });
      fetchCampaigns();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.festival_name}"?`)) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success('Deleted');
      fetchCampaigns();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const isLive = (c) => {
    const now = new Date();
    return c.is_active && new Date(c.start_date) <= now && now <= new Date(c.end_date);
  };

  if (authLoading || !user || user.role !== 'admin') return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-orange-700 hover:text-orange-900" aria-label="Back to dashboard">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-3xl font-bold text-orange-800">Festive Campaigns</h1>
        </div>
        <Button data-testid="add-campaign-btn" onClick={openAdd} className="bg-orange-600 hover:bg-orange-700">
          <Plus size={16} className="mr-1" /> Add Campaign
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-500">
          No campaigns yet. Click <strong>Add Campaign</strong> to create one.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => (
            <div
              key={c.id}
              data-testid={`campaign-${c.id}`}
              className={`bg-white rounded-xl border p-5 ${c.is_active ? '' : 'opacity-60'}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{c.badge_emoji}</span>
                  <div>
                    <p className="font-semibold text-gray-900 leading-tight">{c.festival_name}</p>
                    <span className={`inline-flex items-center gap-1 text-xs ${isLive(c) ? 'text-emerald-700' : 'text-gray-500'}`}>
                      <span className={`w-2 h-2 rounded-full ${isLive(c) ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`}></span>
                      {isLive(c) ? 'LIVE NOW' : (c.is_active ? 'Scheduled' : 'Disabled')}
                    </span>
                  </div>
                </div>
                <span className={`w-4 h-4 rounded-full ${COLOR_SWATCH[c.banner_color] || 'bg-orange-500'}`} title={c.banner_color}></span>
              </div>
              <p className="text-sm text-gray-700 line-clamp-2 mt-2">{c.title}</p>
              <div className="text-xs text-gray-500 mt-3 space-y-1">
                <div className="flex items-center gap-1"><Calendar size={12} /> {new Date(c.start_date).toLocaleDateString()} → {new Date(c.end_date).toLocaleDateString()}</div>
                {c.discount_code && (
                  <div className="flex items-center gap-1"><Tag size={12} /> <span className="font-mono">{c.discount_code}</span> · {c.discount_percent}% off</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-1 mt-4">
                <button onClick={() => toggleActive(c)} className="p-2 hover:bg-gray-100 rounded" title={c.is_active ? 'Disable' : 'Enable'}>
                  <Power size={14} className={c.is_active ? 'text-emerald-600' : 'text-gray-400'} />
                </button>
                <button onClick={() => openEdit(c)} className="p-2 hover:bg-gray-100 rounded">
                  <Edit size={14} />
                </button>
                <button onClick={() => handleDelete(c)} className="p-2 hover:bg-red-50 text-red-600 rounded">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Campaign' : 'Add Campaign'}</DialogTitle>
            <DialogDescription>
              Banners auto-show on the homepage between the chosen start and end dates when the campaign is enabled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>Festival Name *</Label>
              <Input value={form.festival_name} onChange={e => setForm({...form, festival_name: e.target.value})} placeholder="Diwali 2026" />
            </div>
            <div>
              <Label>Banner Headline *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="✨ Diwali Festive Sale — Light Up the Savings" />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={form.subtitle} onChange={e => setForm({...form, subtitle: e.target.value})} placeholder="Up to 25% off on diyas, idols & kits." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount Code</Label>
                <Input value={form.discount_code} onChange={e => setForm({...form, discount_code: e.target.value.toUpperCase()})} placeholder="DIWALI25" />
              </div>
              <div>
                <Label>Discount %</Label>
                <Input type="number" min="0" max="100" value={form.discount_percent} onChange={e => setForm({...form, discount_percent: parseInt(e.target.value || '0', 10)})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date *</Label>
                <Input type="datetime-local" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input type="datetime-local" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Badge Emoji</Label>
                <Input value={form.badge_emoji} onChange={e => setForm({...form, badge_emoji: e.target.value})} maxLength={4} />
              </div>
              <div>
                <Label>Banner Color</Label>
                <select className="w-full border rounded-md p-2 h-10" value={form.banner_color} onChange={e => setForm({...form, banner_color: e.target.value})}>
                  {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <div>
                <Label>Sort Order (lower = priority)</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value || '0', 10)})} />
              </div>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
                Active / enabled
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
